import { MediaPlayerInstance, MediaRemoteControl } from '@vidstack/react';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react';

interface VideoPlayerContextType {
  currentTime: number;
  duration: number;
  canPlay: boolean;
  updateCurrentTime: (time: number) => void;
  updateDuration: (time: number) => void;
  updateCanPlay: (canPlay: boolean) => void;
  moveToTime: (time: number, autoPlay?: boolean) => void;
  onReady: (remote: MediaRemoteControl) => void;
  resetVideoPlayerProps: () => void;
  playerRef: React.RefObject<MediaPlayerInstance> | null;
  setPlayerRef: (ref: React.RefObject<MediaPlayerInstance> | null) => void;
}

const VideoPlayerContext = createContext<VideoPlayerContextType | undefined>(
  undefined,
);

export const useVideoPlayerContext = () => {
  const context = useContext(VideoPlayerContext);

  if (!context) {
    throw new Error(
      'useVideoPlayerContext must be used within a VideoPlayerProvider',
    );
  }

  return context;
};

interface VideoPlayerProviderProps {
  children: ReactNode;
  onCurrentTimeChange?: (time: number) => void;
}
type VideoPlayerProps = {
  currentTime: number;
  duration: number;
  canPlay: boolean;
};
const defaultVideoPlayerProps: VideoPlayerProps = {
  currentTime: 0,
  duration: 0,
  canPlay: false,
};

export const VideoPlayerProvider = ({
  children,
  onCurrentTimeChange,
}: VideoPlayerProviderProps) => {
  const [remote, setRemote] = useState<MediaRemoteControl | undefined>(
    undefined,
  );
  const [videoPlayerProps, setVideoPlayerProps] = useState<VideoPlayerProps>(
    defaultVideoPlayerProps,
  );
  const [pendingSeekTime, setPendingSeekTime] = useState<number | null>(null);
  const [pendingAutoPlay, setPendingAutoPlay] = useState(true);
  const [playerRef, setPlayerRefState] =
    useState<React.RefObject<MediaPlayerInstance> | null>(null);

  const moveToTime = useCallback(
    (time: number, autoPlay = true) => {
      if (videoPlayerProps.canPlay && remote) {
        if (autoPlay) {
          remote.play();
        }
        remote.seek(time);
      } else {
        setPendingSeekTime(time);
        setPendingAutoPlay(autoPlay);
      }
      onCurrentTimeChange?.(time);
    },
    [remote, videoPlayerProps.canPlay, onCurrentTimeChange],
  );
  const onReady = useCallback(
    (remote: MediaRemoteControl) => {
      setRemote(remote);
      if (pendingSeekTime != null && remote) {
        setTimeout(() => {
          if (pendingAutoPlay) {
            remote.play();
          }
          remote.seek(pendingSeekTime);
        }, 1000);
        setPendingSeekTime(null);
        setPendingAutoPlay(true);
      }
    },
    [pendingSeekTime, pendingAutoPlay],
  );
  const updateCurrentTime = useCallback(
    (time: number) => {
      setVideoPlayerProps((prev) => ({ ...prev, currentTime: time }));
      onCurrentTimeChange?.(time);
    },
    [onCurrentTimeChange],
  );
  const updateDuration = useCallback((time: number) => {
    setVideoPlayerProps((prev) => ({ ...prev, duration: time }));
  }, []);
  const updateCanPlay = useCallback((canPlay: boolean) => {
    setVideoPlayerProps((prev) => ({ ...prev, canPlay }));
  }, []);
  const resetVideoPlayerProps = useCallback(() => {
    setVideoPlayerProps(defaultVideoPlayerProps);
    setPendingSeekTime(null);
    setRemote(undefined);
    setPlayerRefState(null);
  }, []);
  const setPlayerRef = useCallback(
    (ref: React.RefObject<MediaPlayerInstance> | null) => {
      setPlayerRefState(ref);
    },
    [],
  );

  return (
    <VideoPlayerContext.Provider
      value={{
        currentTime: videoPlayerProps.currentTime,
        duration: videoPlayerProps.duration,
        canPlay: videoPlayerProps.canPlay,
        updateCurrentTime,
        updateDuration,
        updateCanPlay,
        moveToTime,
        onReady,
        resetVideoPlayerProps,
        playerRef,
        setPlayerRef,
      }}
    >
      {children}
    </VideoPlayerContext.Provider>
  );
};
